// ✅ TaxSection.tsx
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import PageHeader from "../../../core/common/commonComponents/PageHeader";
import StatusFilterHeader from "../../../core/common/commonComponents/StatusFilterHeader";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import TaxSectionModal from "./modal/taxSectionModal";
import { exportToCSV, exportToExcel } from "../../../core/common/GlobalFunctions/exportTableData";
import { AppDispatch } from "../../../core/data/redux/store";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import {
  fetchTdsTaxSections,
  deleteTdsTaxSection,
} from "../../../core/data/redux/payroll/TDS/tdsTaxSection/tdsTaxSectionActions";
import { decryptData } from "../../../core/utils/encryptUtils";

const TaxSection = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { sections } = useSelector((state: any) => state.TDS.tdsTaxSection);

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  // Permission
  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  const showModal = (type: "edit" | "create") => {
    setModalType(type);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setModalType("");
    setIsModalVisible(false);
  };

  useEffect(() => {
    dispatch(fetchTdsTaxSections());
  }, [dispatch]);

  const filteredData = useMemo(() => {
    const filters = selectedStatus !== "All" ? { status: selectedStatus } : {};
    return filterData(sections, filters, "CreatedAt", "CreatedAt", selectedSort);
  }, [sections, selectedStatus, selectedSort]);

  useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [sections, selectedStatus, selectedSort]);

  const exportData = filteredData.map((item: any) => ({
    SectionDescription: item.SectionDescription || "N/A",
    OverallSectionLimit: item.OverallSectionLimit || 0,
    RequiresProof: item.RequiresProof ? "Yes" : "No",
    FinancialYear: item.ApplicableFinancialYearID || "N/A",
    TaxRegimeApplicability: item.TaxRegimeApplicability || "N/A",
    Status: item.Status || "Inactive",
    CreatedBy: item.CreatedBy || "N/A",
  }));

  const handleDelete = async (id: number) => {
    try {
      const result = await dispatch(deleteTdsTaxSection(id));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("Tax section deleted successfully");
        setIsDeleteModalVisible(false);
        dispatch(fetchTdsTaxSections());
      } else {
        message.error("Failed to delete");
      }
    } catch {
      message.error("Something went wrong");
    }
  };

  const columns = [
    {
      title: "Description",
      dataIndex: "SectionDescription",
    },
    {
      title: "Limit",
      dataIndex: "OverallSectionLimit",
    },
    {
      title: "Requires Proof",
      dataIndex: "RequiresProof",
      render: (val: number) => (
        <span className={`badge ${val ? "badge-success" : "badge-secondary"}`}>
          {val ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Financial Year",
      dataIndex: "FinancialYearName",
    },
    {
      title: "Tax Regime",
      dataIndex: "TaxRegimeApplicability",
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (status: string) => (
        <span className={`badge ${status === "active" ? "badge-success" : "badge-danger"}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
    {
      title: "Action",
      render: (_: any, record: any) => (
        <div className="action-icon d-inline-flex">
          {canEdit && (
            <button
              className="me-2"
              onClick={() => {
                showModal("edit");
                setSelectedRowData(record);
              }}
            >
              <i className="ti ti-edit" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => {
                setSelectedRowData(record);
                setIsDeleteModalVisible(true);
              }}
            >
              <i className="ti ti-trash" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <PageHeader
            title="TDS Tax Sections"
            breadcrumbs={[
              { label: <i className="ti ti-smart-home" />, path: all_routes.adminDashboard },
              { label: "TDS" },
              { label: "Tax Sections", isActive: true },
            ]}
            onExportCSV={canExport ? () => exportToCSV(exportData, "TdsTaxSections") : undefined}
            onExportExcel={canExport ? () => exportToExcel(exportData, "TdsTaxSections") : undefined}
            onAdd={canAdd ? () => showModal("create") : undefined}
            addLabel="Add Section"
            exportEnabled={canExport}
            addButtonEnabled={canAdd}
            rightExtra={<CollapseHeader />}
          />

          <div className="card">
            <StatusFilterHeader
              title="Section List"
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
            />
            <div className="card-body p-0">
              <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={false} />
            </div>
          </div>
        </div>
      </div>

      <TaxSectionModal
        modalType={modalType}
        handleCancel={handleCancel}
        isModalVisible={isModalVisible}
        selectedRowData={selectedRowData}
      />

      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.TaxSectionID && handleDelete(selectedRowData?.TaxSectionID)
        }
      />
    </>
  );
};

export default TaxSection;